import type { Profile, SupportedOnboardingTourStep } from 'models';
import { useResetQuery } from 'query';
import { useCallback } from 'react';
import {
  BirthDatePicker,
  type BirthDateType,
  Button,
  Col,
  type FileUploadDone,
  Form,
  ImageDraggerWithCrop,
  PhoneInput,
  Row,
  SelectInput,
  Text,
  Title,
  type UploadChangeParam,
  type UploadedFileList,
  generateUploadFile,
  getBirthDateTypeValue,
  useBirthDateValidation,
  useGenerateImageUploadRulesAndValidations,
} from 'ui';
import { loadImage, timezones } from 'common/utils';
import { uploadImage } from 'features/files';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: fix according to result of #67489
import { useUpdateUserProfile } from 'features/users/queries';
import { useUpdateOnboardingStep } from '../../../queries';

type P = {
  onSuccess: () => void;
  step: SupportedOnboardingTourStep;
  viewer: Profile;
};

type ProfileStepFormValues = {
  birthdate?: BirthDateType;
  phone?: string;
  profilePhoto?: [FileUploadDone];
  timeZone: string;
};

const ProfileStep = ({ onSuccess, step, viewer }: P) => {
  const [form] = Form.useForm<ProfileStepFormValues>();
  const { mutateAsync: resetQuery } = useResetQuery();
  const { isPending: isUpdatingProfile, mutateAsync: updateProfile } = useUpdateUserProfile();
  const { isPending: isUpdatingStep, mutateAsync: updateStep } = useUpdateOnboardingStep();
  const { rules: profilePhotoRules, validations: profilePhotoValidations } =
    useGenerateImageUploadRulesAndValidations();

  const initialValues: Partial<ProfileStepFormValues> = {
    birthdate: getBirthDateTypeValue(viewer.birthdate),
    phone: viewer.phone,
    profilePhoto: viewer.profilePhoto ? generateUploadFile({ response: viewer.profilePhoto }) : undefined,
    timeZone: viewer.timeZone,
  };

  const onFinish = useCallback(
    async (values: ProfileStepFormValues) => {
      await updateProfile({ userData: { ...values, profilePhoto: values.profilePhoto?.[0] }, userId: viewer.id });
      await updateStep({ id: step.data.id, status: 'completed' });
      await resetQuery({ cacheResetOption: 'invalidate' });
      onSuccess();
    },
    [onSuccess, resetQuery, step.data.id, updateProfile, updateStep, viewer.id],
  );

  const birthdateValidatorRule = useBirthDateValidation();

  return (
    <Form<ProfileStepFormValues> className="profile-step" form={form} initialValues={initialValues} onFinish={onFinish}>
      <Row gutter={['lg', 0]}>
        <Col flex="0 0 auto">
          <Form.Item<ProfileStepFormValues>
            getValueFromEvent={({ fileList }: UploadChangeParam): UploadedFileList => fileList}
            label="Avatar"
            name="profilePhoto"
            rules={profilePhotoRules}
            valuePropName="fileList"
          >
            <ImageDraggerWithCrop
              cropper={{ aspect: '1:1', cropShape: 'round' }}
              dragger={{
                loadImage,
                shape: 'circle',
                size: 'small',
                uploadFile: uploadImage,
                validations: profilePhotoValidations,
              }}
            />
          </Form.Item>
        </Col>
        <Col className="profile-step__info" flex={1}>
          <Title level={4}>
            {viewer.firstName} {viewer.lastName}
          </Title>
          <Text>{viewer.email}</Text>
        </Col>
        <Col span={24}>
          <PhoneInput
            input={{
              autoComplete: 'tel',
            }}
            item={{
              label: 'Phone number',
              name: 'phone',
            }}
          />
        </Col>
        <Col span={24}>
          <Form.Item<ProfileStepFormValues> label="Date of birth" name="birthdate" rules={[birthdateValidatorRule({})]}>
            <BirthDatePicker />
          </Form.Item>
        </Col>
        <Col span={24}>
          <SelectInput
            input={{
              getOptionLabel: (option) => option.label,
              getOptionValue: (option) => option.value,
              options: timezones,
              showSearch: true,
            }}
            item={{
              label: 'My timezone',
              name: 'timeZone',
              rules: [
                {
                  required: true,
                },
              ],
            }}
          />
        </Col>
      </Row>
      <Button block htmlType="submit" loading={isUpdatingProfile || isUpdatingStep} size="large" type="primary">
        Continue
      </Button>
    </Form>
  );
};

export default ProfileStep;
